import { FC } from 'react';

import { emailValidation } from '@eneco/common/validation/emailRegex';
import {
  ContactDetailsEmailField,
  ContactDetailsOptIn,
  ContactDetailsPhoneField,
  DUTCH_PHONE_PATTERN,
} from '@eneco/flows/components';
import { usePlaceholderContent } from '@sitecore/common';
import { ContactDetailsStepRendering } from '@sitecore/types/ContactDetailsStep';
import * as yup from 'yup';

import { StepShell } from '../../components/StepShell/StepShell';
import { useFlowStep } from '../../hooks';

interface FormFields {
  phoneNumber: string;
  emailAddress?: string;
  C1?: boolean;
  C2?: boolean;
  C3?: boolean;
}

export const ContactDetailsStep: FC = () => {
  const { ContactDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    ContactDetailsStep: ContactDetailsStepRendering;
  }>();

  const {
    context: { isPersonalDetailsEditProhibited },
    form: {
      handleSubmit,
      register,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<FormFields>({
    buildSchema: () =>
      yup.object({
        phoneNumber: yup
          .string()
          .required(textualData?.contactDetails?.phoneNumberFormField?.value?.requiredMessage)
          .matches(DUTCH_PHONE_PATTERN, textualData?.contactDetails?.phoneNumberFormField?.value?.validationMessage),
        emailAddress: emailValidation({
          emailMessage: textualData?.contactDetails?.emailAddressFormField?.value?.validationMessage,
          requiredMessage: textualData?.contactDetails?.emailAddressFormField?.value?.requiredMessage,
        }),
        C1: yup.boolean(),
        C2: yup.boolean(),
        C3: yup.boolean(),
      }),
    defaultValues: context => ({
      phoneNumber: context.phoneNumber,
      emailAddress: context.emailAddress,
      C1: context.C1 ?? true,
      C2: context.C2 ?? false,
      C3: context.C3,
    }),
    onSubmitSuccess: formData => ({ ...formData }),
  });

  const phoneField = textualData?.contactDetails?.phoneNumberFormField?.value;
  const emailField = textualData?.contactDetails?.emailAddressFormField?.value;

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={textualData?.content?.nextStepText?.value}>
      <ContactDetailsPhoneField
        register={register}
        error={errors.phoneNumber?.message}
        field={{
          label: phoneField?.label,
          hint: phoneField?.hint,
        }}
      />

      <ContactDetailsEmailField
        register={register}
        error={errors.emailAddress?.message}
        field={{
          hint: emailField?.hint,
          label: emailField?.label,
        }}
        note={textualData?.contactDetails?.emailAddressText?.value}
      />

      {!isPersonalDetailsEditProhibited && (
        <ContactDetailsOptIn
          register={register}
          title={textualData?.optIn?.title?.value}
          content={textualData?.optIn?.content?.value}
          headingAs="h2"
          checkboxes={[
            { name: 'C1', label: textualData?.optIn?.checkBoxProductOffersFirstPartyContent?.value },
            { name: 'C2', label: textualData?.optIn?.checkBoxContactByTelephoneContent?.value },
            { name: 'C3', label: textualData?.optIn?.checkBoxProductOffersThirdPartyContent?.value },
          ]}
        />
      )}
    </StepShell>
  );
};
